import { useState } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import Brand from "@/components/ui/brand";
import JsonLd from "@/components/seo/JsonLd";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

const Kontakt = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();

  const contactSchema = z.object({
    name: z.string().trim().min(1, t("contact.nameRequired")).max(100),
    email: z.string().trim().email(t("contact.emailRequired")),
    subject: z.string().trim().min(1, t("contact.subjectRequired")).max(200),
    message: z.string().trim().min(10, t("contact.messageMin")).max(5000),
  });

  type ContactForm = z.infer<typeof contactSchema>;

  const [form, setForm] = useState<ContactForm>({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [sending, setSending] = useState(false);

  const update = (field: keyof ContactForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: typeof errors = {};
      result.error.errors.forEach((err) => {
        const key = err.path[0] as keyof ContactForm;
        if (!fieldErrors[key]) fieldErrors[key] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", { body: result.data });
      if (error || !data?.success) {
        toast.error(data?.error || error?.message || t("contact.sendFailed"));
        return;
      }
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", subject: "", message: "" });
      navigate("/kontakt/danke");
    } catch {
      toast.error(t("contact.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const inputClass = "w-full h-12 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead title={t("contact.seoTitle")} description={t("contact.seoDesc")} canonical="/kontakt" />
      <JsonLd
        includeLocalBusiness
        breadcrumbs={[
          { name: t("common.home", { defaultValue: "Startseite" }), url: "https://www.craftfindus.com/" },
          { name: t("footer.contact"), url: "https://www.craftfindus.com/kontakt" },
        ]}
      />
      <Header />

      <main id="main-content" className="flex-1 pt-28 pb-20 section-padding">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-headline mb-4">{t("contact.title")}</h1>
          <p className="text-caption max-w-xl mb-12">{t("contact.desc", { brand: "CRAFTFINDUS" })}</p>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 space-y-6">
              <a href="tel:+4921224355800" className="group flex items-start gap-4 p-6 border border-border bg-card hover:border-accent/40 transition-all duration-300">
                <Phone className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                <div>
                  <h2 className="text-body font-medium mb-1">{t("contact.phoneLabel")}</h2>
                  <p className="text-caption group-hover:text-foreground transition-colors">0212 24355800</p>
                </div>
              </a>
              <a href="mailto:kontakt@craftfindus.de" className="group flex items-start gap-4 p-6 border border-border bg-card hover:border-accent/40 transition-all duration-300">
                <Mail className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                <div>
                  <h2 className="text-body font-medium mb-1">{t("contact.emailLabel")}</h2>
                  <p className="text-caption group-hover:text-foreground transition-colors">kontakt@craftfindus.de</p>
                </div>
              </a>
            </div>

            <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-5" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label htmlFor="kontakt-name" className="text-micro block">{t("contact.nameLabel")}</label>
                  <input id="kontakt-name" type="text" autoComplete="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={t("contact.namePlaceholder")} className={inputClass} aria-invalid={!!errors.name} aria-describedby={errors.name ? "kontakt-name-error" : undefined} required />
                  {errors.name && <p id="kontakt-name-error" className="text-xs text-destructive" role="alert">{errors.name}</p>}
                </div>
                <div className="space-y-2">
                  <label htmlFor="kontakt-email" className="text-micro block">{t("contact.emailFieldLabel")}</label>
                  <input id="kontakt-email" type="email" autoComplete="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder={t("contact.emailPlaceholder")} className={inputClass} aria-invalid={!!errors.email} aria-describedby={errors.email ? "kontakt-email-error" : undefined} required />
                  {errors.email && <p id="kontakt-email-error" className="text-xs text-destructive" role="alert">{errors.email}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="kontakt-subject" className="text-micro block">{t("contact.subjectLabel")}</label>
                <input id="kontakt-subject" type="text" value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder={t("contact.subjectPlaceholder")} className={inputClass} aria-invalid={!!errors.subject} aria-describedby={errors.subject ? "kontakt-subject-error" : undefined} required />
                {errors.subject && <p id="kontakt-subject-error" className="text-xs text-destructive" role="alert">{errors.subject}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="kontakt-message" className="text-micro block">{t("contact.messageLabel")}</label>
                <textarea id="kontakt-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={t("contact.messagePlaceholder")} rows={6} className="w-full px-4 py-3 border border-border bg-background text-body focus:border-primary transition-colors resize-none" aria-invalid={!!errors.message} aria-describedby={errors.message ? "kontakt-message-error" : undefined} required />
                {errors.message && <p id="kontakt-message-error" className="text-xs text-destructive" role="alert">{errors.message}</p>}
              </div>

              <Button type="submit" variant="commit" size="lg" disabled={sending} className="gap-2">
                <Send className="w-4 h-4" />
                {sending ? t("contact.sending") : t("contact.sendBtn")}
              </Button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Kontakt;
